import * as Yup from 'yup';

const objectIdRegex = /^[a-f\d]{24}$/i;

const defaultRMVendorItem = {
    propertyId: '',
    requestsSupported: '',
    make: '',
    modelNo: '',
    sku: '',
    images: [],
    costPerUnit: '',
    description: '',
    quantity: {
        min: '',
        max: '',
    },
};

const defaultRMVendor = {
    companyName: '',
    contactName: '',
    email: '',
    phone: {
        countryCode: '',
        number: '',
    },
    requestsSupported: [],
    items: [defaultRMVendorItem],
    address: {
        street: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
    },
    password: '',
    isActive: true,
};

export const rmVendorItemSchema = Yup.object().shape({
    propertyId: Yup.string()
        .test('is-object-id', 'Invalid property ID', (value) => !value || objectIdRegex.test(value))
        .required('Property ID is required'),
    requestsSupported: Yup.string()
        .test('is-object-id', 'Invalid request type ID', (value) => !value || objectIdRegex.test(value))
        .optional(),
    make: Yup.string().optional(),
    modelNo: Yup.string().optional(),
    sku: Yup.string().optional(),
    images: Yup.array().of(
        Yup.object().shape({
            url: Yup.string().url('Invalid image URL').required('Image URL is required'),
            uploadedBy: Yup.object().shape({
                id: Yup.string()
                    .test('is-object-id', 'Invalid ID', value => !value || objectIdRegex.test(value))
                    .optional(),
                role: Yup.string().optional(),
            }).required('Uploaded by information is required'),
        })
    ).optional(),
    costPerUnit: Yup.number().optional().min(0, 'Cost cannot be negative'),
    description: Yup.string().optional(),
    quantity: Yup.object()
        .shape({
            min: Yup.number().optional().min(0, 'Minimum quantity cannot be negative'),
            max: Yup.number()
                .optional()
                .min(0, 'Maximum quantity cannot be negative')
                .test(
                    'max-greater-than-min',
                    'Maximum quantity must be greater than or equal to minimum quantity',
                    function (value) {
                        const { min } = this.parent;
                        if (min !== undefined && value !== undefined) {
                            return value >= min;
                        }
                        return true;
                    }
                ),
        })
        .optional(),
});

export const rmVendorSchema = Yup.object().shape({
    primaryId: Yup.number().nullable().optional(),
    companyName: Yup.string().required('Company name is required'),
    contactName: Yup.string().required('Contact name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    phone: Yup.object()
        .shape({
            countryCode: Yup.string().required('Country code is required'),
            number: Yup.string().required('Phone number is required'),
        })
        .required('Phone is required'),
    requestsSupported: Yup.array()
        .of(Yup.string().test('is-object-id', 'Invalid request type ID', (value) => objectIdRegex.test(value)))
        .min(1, 'At least one supported request type is required'),
    items: Yup.array().of(rmVendorItemSchema).optional(),
    address: Yup.object()
        .shape({
            street: Yup.string().optional(),
            city: Yup.string().optional(),
            province: Yup.string().optional(),
            postalCode: Yup.string().optional(),
            country: Yup.string().optional(),
        })
        .required('Address is required'),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .optional(),
    isActive: Yup.boolean().optional(),
});

export const updateRMVendorSchema = Yup.object().shape({
    primaryId: Yup.number().nullable().optional(),
    companyName: Yup.string().required('Company name is required'),
    contactName: Yup.string().required('Contact name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    phone: Yup.object()
        .shape({
            countryCode: Yup.string().required('Country code is required'),
            number: Yup.string().required('Phone number is required'),
        })
        .required('Phone is required'),
    requestsSupported: Yup.array()
        .of(Yup.string().test('is-object-id', 'Invalid request type ID', (value) => objectIdRegex.test(value)))
        .min(1, 'At least one supported request type is required'),
    items: Yup.array().of(rmVendorItemSchema).optional(),
    address: Yup.object()
        .shape({
            street: Yup.string().optional(),
            city: Yup.string().optional(),
            province: Yup.string().optional(),
            postalCode: Yup.string().optional(),
            country: Yup.string().optional(),
        })
        .required('Address is required'),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .optional(),
    isActive: Yup.boolean().optional(),
});

export const getValidationSchema = (mode) => {
    switch (mode) {
        case 'add':
            return rmVendorSchema;
        case 'edit':
            return updateRMVendorSchema;
        default:
            return rmVendorSchema;
    }
};

export const getInitialValues = (mode, selectedData = null, preservedValues = {}) => {
    if (mode === 'add') {
        return {
            ...defaultRMVendor,
            ...preservedValues,
        };
    }

    if (mode === 'edit' && selectedData) {
        return {
            companyName: selectedData.companyName || '',
            contactName: selectedData.contactName || '',
            email: selectedData.email || '',
            phone: {
                countryCode: selectedData.phone?.countryCode || '',
                number: selectedData.phone?.number || '',
            },
            requestsSupported: selectedData.requestsSupported?.map((req) =>
                typeof req === 'object' ? req._id : req
            ) || [],
            items: selectedData.items?.map((item) => ({
                propertyId: item.propertyId?._id || item.propertyId || '',
                requestsSupported: typeof item.requestsSupported === 'object' ? item.requestsSupported?._id : item.requestsSupported || '',
                make: item.make || '',
                modelNo: item.modelNo || '',
                sku: item.sku || '',
                images: item.images || [],
                costPerUnit: item.costPerUnit || '',
                description: item.description || '',
                quantity: {
                    min: item.quantity?.min || '',
                    max: item.quantity?.max || '',
                },
            })) || [defaultRMVendorItem],
            address: {
                street: selectedData.address?.street || '',
                city: selectedData.address?.city || '',
                province: selectedData.address?.province || '',
                postalCode: selectedData.address?.postalCode || '',
                country: selectedData.address?.country || '',
            },
            password: '',
            isActive: selectedData.isActive !== undefined ? selectedData.isActive : true,
            ...preservedValues,
        };
    }

    return {
        ...defaultRMVendor,
        ...preservedValues,
    };
};